import { useMemo } from "react";
import { TiTick } from "react-icons/ti";
import { RxCross2 } from "react-icons/rx";
import { Pagination } from "../atoms";

export default function BillList({
  bills = [],
  currentPage,
  pageSize,
  onPageChange,
  state,
}) {
  const paginatedData = useMemo(
    () => bills.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [bills, currentPage, pageSize]
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Desktop Table */}
      <div className="hidden md:flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wider bg-gray-50 w-36">
                  Fatura No
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wider bg-gray-50">
                  Firma Bilgileri
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wider bg-gray-50">
                  Ürün Bilgileri
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wider bg-gray-50 w-48">
                  Finansal Detaylar
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 tracking-wider bg-gray-50 w-28">
                  Durum
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedData.map((item, index) => (
                <tr
                  key={item._id || `${item.billNo}-${index}`}
                  className="hover:bg-gray-50/60 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-blue-600">
                          {index + 1 + (currentPage - 1) * pageSize}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {item.billNo}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {item.createdInfo
                            ? new Date(item.createdInfo).toLocaleDateString(
                                "tr-TR"
                              )
                            : "—"}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                      {item.firmApellation}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {item.productName}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      Kod: {item.productCode}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 bg-gray-50 rounded-md px-2 py-1 text-[11px]">
                        <span className="text-gray-400">Kota</span>
                        <span className="font-semibold text-gray-700">
                          {item.quota}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 bg-blue-50 rounded-md px-2 py-1 text-[11px]">
                        <span className="text-blue-400">Fiyat</span>
                        <span className="font-semibold text-blue-700">
                          {item.price?.toFixed(2)}₺
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 bg-green-50 rounded-md px-2 py-1 text-[11px]">
                        <span className="text-green-400">Gelir</span>
                        <span className="font-semibold text-green-700">
                          {item.income?.toFixed(2)}₺
                        </span>
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        item.status
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.status ? (
                        <TiTick className="w-3.5 h-3.5" />
                      ) : (
                        <RxCross2 className="w-3.5 h-3.5" />
                      )}
                      {item.status ? "Başarılı" : "Hatalı"}
                    </span>
                    {item.message && (
                      <p
                        className="text-[10px] text-gray-400 mt-1 max-w-[120px] mx-auto line-clamp-2"
                        title={item.message}
                      >
                        {item.message}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden flex-1 overflow-auto p-3 space-y-2.5">
        {paginatedData.map((item, index) => (
          <div
            key={item._id || `${item.billNo}-${index}`}
            className="bg-white border border-gray-200 rounded-xl p-3.5 space-y-2.5"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-blue-600">
                    {index + 1 + (currentPage - 1) * pageSize}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {item.billNo}
                </span>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                  item.status
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {item.status ? (
                  <TiTick className="w-3 h-3" />
                ) : (
                  <RxCross2 className="w-3 h-3" />
                )}
                {item.status ? "Başarılı" : "Hatalı"}
              </span>
            </div>
            <div className="text-xs text-gray-600 truncate">
              {item.firmApellation}
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className="text-[11px] bg-gray-50 px-2 py-0.5 rounded">
                Kota: <b>{item.quota}</b>
              </span>
              <span className="text-[11px] bg-blue-50 px-2 py-0.5 rounded text-blue-700">
                {item.price?.toFixed(2)}₺
              </span>
              <span className="text-[11px] bg-green-50 px-2 py-0.5 rounded text-green-700">
                {item.income?.toFixed(2)}₺
              </span>
            </div>
            {item.message && (
              <p className="text-[10px] text-gray-400">{item.message}</p>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {bills.length > pageSize && (
        <div className="shrink-0 border-t border-gray-200 bg-white">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(bills.length / pageSize)}
            onPageChange={onPageChange}
            itemsPerPage={pageSize}
            totalItems={bills.length}
            showInfo={true}
          />
        </div>
      )}
    </div>
  );
}
